import { formatUnreadCount } from '../storage/unreadStore.ts'
import { deleteChatForMe } from '../app/zionApp.ts'
import { isChatPinned, toggleChatPinned } from '../storage/pinnedChatsStore.ts'
import { t } from '../i18n/index.ts'
import { el, escapeHtml } from './dom.ts'

const SWIPE_MAX = 100
const SWIPE_THRESHOLD = 70
const LONG_PRESS_MS = 480

let modalEl: HTMLElement | null = null
let pinMenuEl: HTMLElement | null = null

export type ChatRowOptions = {
  contactId: string
  name: string
  subtitle: string
  avatarInitial: string
  unread: number
  hasUnread: boolean
  isPinned?: boolean
  tempChatLabel: string
  onOpen: () => void
  onTempChat: () => void
  onPinChanged?: () => void
}

function ensureDeleteModal(): HTMLElement {
  if (modalEl?.isConnected) return modalEl
  modalEl = el(`
    <div class="chat-delete-modal hidden" id="chat-delete-modal" hidden role="dialog" aria-modal="true" aria-labelledby="chat-delete-modal-title">
      <div class="chat-delete-dialog">
        <h3 id="chat-delete-modal-title">${escapeHtml(t('home_swipe_delete_title'))}</h3>
        <p>${escapeHtml(t('home_swipe_delete_body'))}</p>
        <div class="row-btns">
          <button type="button" class="secondary" id="chat-delete-cancel">${escapeHtml(t('home_swipe_delete_cancel'))}</button>
          <button type="button" class="danger" id="chat-delete-confirm">${escapeHtml(t('home_swipe_delete_confirm'))}</button>
        </div>
      </div>
    </div>
  `)
  document.body.append(modalEl)
  return modalEl
}

function showDeleteModal(contactId: string, onCancel: () => void): void {
  const modal = ensureDeleteModal()
  const cancelBtn = modal.querySelector('#chat-delete-cancel') as HTMLButtonElement
  const confirmBtn = modal.querySelector('#chat-delete-confirm') as HTMLButtonElement

  const close = (reset: boolean) => {
    modal.classList.add('hidden')
    modal.hidden = true
    cancelBtn.onclick = null
    confirmBtn.onclick = null
    modal.onclick = null
    if (reset) onCancel()
  }

  modal.classList.remove('hidden')
  modal.hidden = false
  cancelBtn.onclick = () => close(true)
  confirmBtn.onclick = () => {
    close(false)
    void deleteChatForMe(contactId)
  }
  modal.onclick = (e) => {
    if (e.target === modal) close(true)
  }
}

function ensurePinMenu(): HTMLElement {
  if (pinMenuEl?.isConnected) return pinMenuEl
  pinMenuEl = el(`
    <div class="chat-pin-menu hidden" id="chat-pin-menu" hidden role="menu">
      <button type="button" class="chat-pin-menu-item" id="chat-pin-toggle"></button>
      <button type="button" class="chat-pin-menu-item secondary" id="chat-pin-cancel">${escapeHtml(t('chat_menu_cancel'))}</button>
    </div>
  `)
  document.body.append(pinMenuEl)
  return pinMenuEl
}

function showPinMenu(contactId: string, onChanged?: () => void): void {
  const menu = ensurePinMenu()
  const toggleBtn = menu.querySelector('#chat-pin-toggle') as HTMLButtonElement
  const cancelBtn = menu.querySelector('#chat-pin-cancel') as HTMLButtonElement
  const pinned = isChatPinned(contactId)
  toggleBtn.textContent = pinned ? t('chat_unpin') : t('chat_pin')

  const close = () => {
    menu.classList.add('hidden')
    menu.hidden = true
    toggleBtn.onclick = null
    cancelBtn.onclick = null
    menu.onclick = null
  }

  menu.classList.remove('hidden')
  menu.hidden = false
  toggleBtn.onclick = () => {
    toggleChatPinned(contactId)
    close()
    onChanged?.()
  }
  cancelBtn.onclick = () => close()
  menu.onclick = (e) => {
    if (e.target === menu) close()
  }
}

/** Riga chat con swipe touch/mouse auto-contenuto. */
export function createChatRow(opts: ChatRowOptions): HTMLElement {
  const pinned = !!opts.isPinned
  const row = el(`
    <div class="list-item chat-row${opts.hasUnread ? ' has-unread' : ''}${pinned ? ' is-pinned' : ''}" data-id="${escapeHtml(opts.contactId)}">
      <div class="chat-row-gradient" aria-hidden="true">
        <span class="chat-row-delete-label">${escapeHtml(t('home_swipe_delete_label'))}</span>
      </div>
      <div class="chat-row-slide">
        <button type="button" class="chat-row-open">
          <span class="avatar">${escapeHtml(opts.avatarInitial)}</span>
          <span class="chat-row-text">
            <span class="chat-row-name">${pinned ? `<span class="chat-pin-badge" title="${escapeHtml(t('chat_pinned'))}" aria-hidden="true">📌</span> ` : ''}${escapeHtml(opts.name)}</span>
            <span class="chat-row-sub">${escapeHtml(opts.subtitle)}</span>
          </span>
        </button>
        <div class="chat-row-meta">
          <button type="button" class="icon-btn chat-pin-btn" title="${escapeHtml(pinned ? t('chat_unpin') : t('chat_pin'))}" aria-label="${escapeHtml(pinned ? t('chat_unpin') : t('chat_pin'))}">${pinned ? '📌' : '📍'}</button>
          <button type="button" class="icon-btn icon-btn-clock temp-chat-btn" title="${escapeHtml(opts.tempChatLabel)}" aria-label="${escapeHtml(opts.tempChatLabel)}"></button>
          ${opts.unread > 0 ? `<span class="unread-badge">${escapeHtml(formatUnreadCount(opts.unread))}</span>` : ''}
        </div>
      </div>
    </div>
  `)

  const gradient = row.querySelector('.chat-row-gradient') as HTMLElement
  const deleteLabel = row.querySelector('.chat-row-delete-label') as HTMLElement
  const slide = row.querySelector('.chat-row-slide') as HTMLElement
  const openBtn = row.querySelector('.chat-row-open') as HTMLButtonElement
  const tempBtn = row.querySelector('.temp-chat-btn') as HTMLButtonElement
  const pinBtn = row.querySelector('.chat-pin-btn') as HTMLButtonElement

  let startX = 0
  let currentX = 0
  let isSwiping = false
  let blockOpen = false
  let longPressTimer: number | null = null
  let longPressFired = false

  const clearLongPress = (): void => {
    if (longPressTimer != null) {
      window.clearTimeout(longPressTimer)
      longPressTimer = null
    }
  }

  const armLongPress = (): void => {
    clearLongPress()
    longPressFired = false
    longPressTimer = window.setTimeout(() => {
      longPressFired = true
      blockOpen = true
      showPinMenu(opts.contactId, opts.onPinChanged)
    }, LONG_PRESS_MS)
  }

  const resetSwipe = (): void => {
    isSwiping = false
    currentX = 0
    blockOpen = false
    slide.style.transition = 'transform 0.22s ease'
    slide.style.transform = 'translateX(0)'
    gradient.style.background = 'linear-gradient(to left, rgba(255, 59, 48, 0), transparent)'
    deleteLabel.style.opacity = '0'
  }

  const applyDrag = (diffX: number): void => {
    if (diffX >= 0) {
      currentX = 0
      slide.style.transform = 'translateX(0)'
      gradient.style.background = 'linear-gradient(to left, rgba(255, 59, 48, 0), transparent)'
      deleteLabel.style.opacity = '0'
      return
    }
    const shift = Math.max(-SWIPE_MAX, diffX)
    currentX = shift
    slide.style.transform = `translateX(${shift}px)`
    const opacity = Math.min(Math.abs(shift) / 80, 0.8)
    gradient.style.background = `linear-gradient(to left, rgba(255, 59, 48, ${opacity}), transparent)`
    deleteLabel.style.opacity = String(Math.min(1, Math.abs(shift) / 40))
  }

  const finishDrag = (diffX: number): void => {
    isSwiping = false
    slide.style.transition = 'transform 0.22s ease'
    if (diffX < -SWIPE_THRESHOLD) {
      showDeleteModal(opts.contactId, resetSwipe)
      return
    }
    resetSwipe()
  }

  const onTouchStart = (e: TouchEvent): void => {
    if ((e.target as HTMLElement).closest('.chat-row-meta button')) return
    const touch = e.touches[0]
    if (!touch) return
    startX = touch.clientX
    currentX = 0
    isSwiping = true
    blockOpen = false
    slide.style.transition = 'none'
    armLongPress()
  }

  const onTouchMove = (e: TouchEvent): void => {
    if (!isSwiping) return
    const touch = e.touches[0]
    if (!touch) return
    const diffX = touch.clientX - startX
    if (Math.abs(diffX) > 8) clearLongPress()
    if (diffX < -4) {
      e.preventDefault()
      blockOpen = true
      clearLongPress()
    }
    applyDrag(diffX)
  }

  const onTouchEnd = (e: TouchEvent): void => {
    clearLongPress()
    if (longPressFired) {
      isSwiping = false
      resetSwipe()
      return
    }
    if (!isSwiping) return
    const touch = e.changedTouches[0]
    if (!touch) return
    finishDrag(touch.clientX - startX)
  }

  row.addEventListener('touchstart', onTouchStart, { passive: true })
  row.addEventListener('touchmove', onTouchMove, { passive: false })
  row.addEventListener('touchend', onTouchEnd)
  row.addEventListener('touchcancel', onTouchEnd)

  row.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('.chat-row-meta button')) return
    startX = e.clientX
    currentX = 0
    isSwiping = true
    blockOpen = false
    slide.style.transition = 'none'

    const onMouseMove = (ev: MouseEvent) => {
      const diffX = ev.clientX - startX
      if (diffX < -4) blockOpen = true
      applyDrag(diffX)
    }
    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      if (!isSwiping) return
      finishDrag(ev.clientX - startX)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  })

  openBtn.addEventListener('click', (e) => {
    if (longPressFired || blockOpen || currentX < -8) {
      e.preventDefault()
      blockOpen = false
      longPressFired = false
      resetSwipe()
      return
    }
    opts.onOpen()
  })

  openBtn.addEventListener('contextmenu', (e) => {
    e.preventDefault()
    showPinMenu(opts.contactId, opts.onPinChanged)
  })

  pinBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    toggleChatPinned(opts.contactId)
    opts.onPinChanged?.()
  })

  tempBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    opts.onTempChat()
  })

  return row
}
